import { UiEvent } from '../utils/ui-event';

interface TrackingConfig {
	selector: string;
	subcategory: string;
	targetId: string;
	once?: boolean;
}

export class GlobalClickTrackingEditorial {
	constructor() {
		this.initializeTracking();
	}

	private isDebugMode(): boolean {
		return localStorage.getItem('debug') === 'true';
	}

	private initializeTracking(): void {
		const trackingConfigs: TrackingConfig[] = [
			{
				selector: `
				  .navigation a,
				  .sticky-navigation a,
				  .fixed-navigation a,
				  .menu-mobile-navigation a,
				  .header-sticky a,
				  .header-transparent a,
				  #masthead a
				`,
				subcategory: 'header',
				targetId: 'main-navigation',
			},
			{
				selector: '[data-bi-id="mobile-navigation-toggle"]',
				subcategory: 'header',
				targetId: 'menu-button',
			},
			{
				selector: `
					[data-bi-id="content"] a:not(.outro a, .table-of-content li a, .scroll-slider a)
				`,
				subcategory: 'content',
				targetId: 'inline-link',
			},
			{
				selector: '.table-of-content li a',
				subcategory: 'content',
				targetId: 'table-of-content',
			},
			{
				selector: '.outro .tabs button',
				subcategory: 'content',
				targetId: 'tab-button',
			},
			{
				selector: '.outro .tabs a:not(.footnotes a)',
				subcategory: 'content',
				targetId: 'tab-content',
			},
			{
				selector: `
					.outro :is(.linkbox, .related-articles, .footnotes) a,
					.widget-area-outro a
				`,
				subcategory: 'content',
				targetId: 'linkbox',
			},
			{
				selector: 'lite-youtube',
				subcategory: 'content',
				targetId: 'youtube',
			},
			{
				selector: '.pagination a',
				subcategory: 'content',
				targetId: 'pagination',
			},
			{
				selector: '.breadcrumbs a',
				subcategory: 'content',
				targetId: 'breadcrumb',
			},
			{
				selector: 'details summary',
				subcategory: 'content',
				targetId: 'details-summary',
			},
			{
				selector: '.wp-block-psg-survey button:not(.show-results) .content',
				subcategory: 'content',
				targetId: 'survey',
			},
			{
				selector: `
					.wp-block-psg-quiz .quiz-intro__cta,
					.wp-block-psg-quiz label,
					.wp-block-psg-quiz a
				`,
				subcategory: 'content',
				targetId: 'quiz',
			},
			{
				selector: `
				[data-bi-id="content"] :is(
					.scroll-slider button,
					.scroll-slider a
					)
				`,
				subcategory: 'content',
				targetId: 'slider',
				once: false,
			},
			{
				selector: '.internal-links :is(a, button)',
				subcategory: 'footer',
				targetId: 'internal-links',
			},
			{
				selector: '.external-links a',
				subcategory: 'footer',
				targetId: 'external-links',
			},
		];

		trackingConfigs.forEach((config: TrackingConfig) => this.trackClick(config));
	}

	private trackClick({
		selector,
		subcategory = 'content',
		targetId = '',
		once = true,
	}: TrackingConfig): void {
		document.querySelectorAll(selector).forEach((element: HTMLElement) => {
			if (this.isDebugMode()) {
				const { cd1, cd2 } = this.extractTrackingData(element, targetId);
				if (cd1 === '' && cd2 === '') {
					// eslint-disable-next-line no-console
					console.log('No tracking data found', element, selector);
				}
			}

			element.addEventListener(
				'click',
				() => {
					const { cd1, cd2 } = this.extractTrackingData(element, targetId);

					if (cd1 === '' && cd2 === '') {
						return;
					}
					UiEvent.trackUIEvent(subcategory, targetId, 'click', cd1, cd2);
				},
				{ once }
			);

			if (this.isDebugMode()) {
				if (element.style.outline && element.style.boxShadow) {
					// eslint-disable-next-line no-console
					console.log('Element has more than one click tracking', element, selector);
				}
				element.style.setProperty('outline', `#001eff dashed 2px`);
				element.style.setProperty('box-shadow', `0 0 0 2px #f000ff`);
			}
		});
	}

	private extractTrackingData(
		element: HTMLElement,
		targetId: string
	): { cd1: string; cd2: string } {
		let cd1: string = '';
		let cd2: string = '';

		if (targetId === 'quiz') {
			cd1 = element.closest('form')?.id || '';
			switch (element.tagName) {
				case 'BUTTON':
					cd2 = 'start';
					break;
				case 'LABEL':
					const questionList: HTMLElement = element.closest('.questions-list');
					const questionItem: HTMLElement = element.closest('.questions-list__item');
					const totalCount: number = questionList?.childElementCount || 0;
					cd2 = `${questionItem?.getAttribute('data-question-id')}/${totalCount}`;
					break;
				case 'A':
					cd2 = 'CTA';
					break;
			}
		} else if (targetId === 'slider') {
			switch (element.tagName) {
				case 'BUTTON':
					cd1 = element.getAttribute('aria-label') || element.getAttribute('class') || '';
					break;
				case 'A':
					cd1 = element.getAttribute('href') || '';
					cd2 = 'link';
					break;
			}
		} else {
			switch (element.tagName) {
				case 'A':
					cd1 = element.getAttribute('href') || '';
					cd2 = element.textContent?.trim() || '';
					break;
				case 'BUTTON':
					cd1 = element.getAttribute('data-href') || '';
					cd2 =
						element.getAttribute('aria-label') ||
						element.getAttribute('title') ||
						element.textContent?.trim() ||
						'';
					break;
				case 'LITE-YOUTUBE':
					cd1 = element.getAttribute('videoid') || '';
					break;
				case 'SUMMARY':
					cd1 = element.textContent?.trim() || '';
					cd2 = element.parentElement?.hasAttribute('open') ? 'close' : 'open';
					break;
				case 'SPAN':
				case 'DIV':
					cd1 = element.textContent?.trim() || '';
					break;
				default:
					if (this.isDebugMode()) {
						// eslint-disable-next-line no-console
						console.log('No tracking data found', element, targetId);
					}
			}
		}

		return { cd1, cd2 };
	}
}
